import { useState } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { Spinner } from "flowbite-react";
import { AppApprovalForm } from "@/features/auth/components/LoginForm/Forms/AppApprovalForm";
import { FirstSignInInfo } from "@/features/auth/components/LoginForm/Forms/FirstSignInInfo";
import { LoginForm } from "@/features/auth/components/LoginForm/Forms/LoginForm";
import { MissingCellphoneForm } from "@/features/auth/components/LoginForm/Forms/MissingCellphoneForm";
import { TwoFAForm } from "@/features/auth/components/LoginForm/Forms/TwoFAForm";
import { useAuth } from "@/features/auth/hooks/useAuth";
import type { LoginOnboardingTrail } from "@/features/auth/hooks/useLoginOnboardingTrail";
import { type LoginFormData, loginFormSchema } from "@/features/auth/schemas/forms/loginForm.schema";
import { LegalComplianceRefusalAlert } from "@/features/legal/components/LegalComplianceRefusalAlert";
import { LegalComplianceOnboarding } from "@/features/legal/components/onboarding/LegalComplianceOnboarding";
import { PostLoginOnboardingLayout } from "@/features/legal/components/onboarding/PostLoginOnboardingLayout";
import { ONBOARDING_COPY } from "@/features/legal/config/onboarding.config";
import type { OnboardingPreferences } from "@/features/legal/types/onboarding.type";
import type { UserResponse } from "@/shared/types";

const SESSION_LOADING_LABEL = "Chargement de votre session...";

const pickPreferences = (user: UserResponse | null): OnboardingPreferences => ({
  allow_use_cell_phone: user?.employee.allow_use_cell_phone === true,
  allow_show_cell_phone: user?.employee.allow_show_cell_phone === true,
  allow_email_from_yeati: user?.employee.allow_email_from_yeati === true,
});

const LoadingState = ({ label }: { label: string }) => (
  <div className="flex flex-col items-center justify-center gap-2 py-4" aria-live="polite">
    <span className="text-sm text-gray-600 dark:text-gray-300">{label}</span>
    <Spinner />
  </div>
);

type LoginFormContainerProps = { trail: LoginOnboardingTrail };

// Stays mounted for the whole login: the security steps need the credentials
// typed in the first form.
export const LoginFormContainer = ({ trail }: LoginFormContainerProps) => {
  const {
    login, clearError, logout, user, isLegalComplianceRequired, completeLegalCompliance,
  } = useAuth();
  const [hasRefusedLegalCompliance, setHasRefusedLegalCompliance] = useState(false);

  const loginForm = useForm<LoginFormData>({
    resolver: zodResolver(loginFormSchema),
    mode: "onSubmit",
    defaultValues: {
      email: "",
      password: "",
      hcaptcha: "",
    },
  });

  const onSubmitForm = async (data: LoginFormData) => {
    setHasRefusedLegalCompliance(false);
    clearError();
    await login(data);
  };

  // A failed server-side revocation must not block the refusal UI: the
  // local access token is already cleared by the time this runs. The
  // credentials typed earlier are dropped with the session.
  const attemptLogout = async () => {
    loginForm.reset();
    try {
      await logout();
    }
    catch {
      // Ignored: see comment above.
    }
  };

  const handleLegalComplianceRefused = async () => {
    setHasRefusedLegalCompliance(true);
    await attemptLogout();
  };

  if (isLegalComplianceRequired) {
    return (
      <LegalComplianceOnboarding
        hasSecurityStep={trail.hasSecurityStep}
        initialPreferences={pickPreferences(user)}
        cellPhoneNumber={user?.employee.cell_phone_number ?? null}
        onCompleted={completeLegalCompliance}
        onRefused={handleLegalComplianceRefused}
        onLogout={attemptLogout}
      />
    );
  }

  if (trail.isOnboardingActive) {
    return (
      <PostLoginOnboardingLayout
        currentStep={trail.isSecurityRequired ? "security" : "ready"}
        hasSecurityStep={trail.hasSecurityStep}
        hasLegalSteps={trail.hasLegalSteps}
      >
        {trail.isSessionLoading
          ? <LoadingState label={trail.isSecurityRequired ? SESSION_LOADING_LABEL : ONBOARDING_COPY.LOADING_SPACE} />
          : (
            <>
              <AppApprovalForm onBackToLogin={attemptLogout} />
              <TwoFAForm loginData={loginForm.getValues()} onBackToLogin={attemptLogout} />
              <MissingCellphoneForm loginData={loginForm.getValues()} onBackToLogin={attemptLogout} />
            </>
          )}
      </PostLoginOnboardingLayout>
    );
  }

  return (
    <>
      {hasRefusedLegalCompliance && <LegalComplianceRefusalAlert />}
      {trail.isSessionLoading
        ? <LoadingState label={SESSION_LOADING_LABEL} />
        : (
          <>
            <LoginForm form={loginForm} onSubmitForm={onSubmitForm} />
            <FirstSignInInfo />
          </>
        )}
    </>
  );
};
